import { translate } from '~l10n/client';
/*
 * This is a special controller designed to listen for activity changes on behalf
 * of all other student controllers. This controller renders no view. Instead, it
 * redirects to appropriate routes in response to activity changes.
 */
import Pubnub from 'pubnub';

import { shuffler } from '../../collection';

// To avoid a direct circular dependecy, we don't require('student-router').
let router = null;

let RootStudentModel = require('root-student-model');
let model = new RootStudentModel();
let Constants = require('Constants');
let messaging = require('Messaging');
let activity = require('activity');
let room = require('room');
let user = require('user');
let header = require('StudentHeaderController');
let popup = require('../../../../shared/components/popup/PopupController');
let ReactDOM = require('react-dom');
let utils = require('Utils');

function RootStudentController() {}

RootStudentController.prototype = {
  doRootStudent: function (studentRouter) {
    router = studentRouter;
    if (!controller.isReady()) {
      controller.initialize();
      return;
    }
    controller.doNextRoute();
  },

  doJoinRoom: function (studentRouter, roomName) {
    router = studentRouter;
    model.joinRoom(roomName, controller.joinRoomResult);
  },

  joinRoomResult: function (success) {
    if (success) {
      controller.initialize();
    } else {
      popup.render({
        title: translate('Invalid Room'),
        message: translate('Please check the room name and try again.'),
        buttonClicked: function () {
          router.routeToStudentLogin();
        },
        cancelClicked: function () {
          router.routeToStudentLogin();
        },
      });
    }
  },

  isReady: function () {
    return model.isReady();
  },

  initialize: function () {
    user.setModeSilent('student');
    if (!model.studentLoggedIn()) {
      router.routeToStudentLogin();
      return;
    }

    if (model.requiresRosterLogin()) {
      controller.redirectToStudentIdRoute();
      return;
    }

    room.setRoomName(user.getRoomName());

    if (!model.get('listening')) {
      // Setup pubnub if it hasn't been setup yet
      if (!window.pubnub) {
        let pubnubOptions = {
          subscribeKey: `${window.APP_CONFIG.PUBNUB_SUBSCRIBE_KEY}`,
          origin: 'pubsub.pubnub.com',
          ssl: true,
        };

        let uuid = user.get('user_uuid');
        if (uuid) {
          pubnubOptions['uuid'] = uuid;
        }
        window.pubnub = new Pubnub(pubnubOptions);
      }

      model.listenTo(activity, 'change', controller.activityChanged);

      if (model.studentHasId()) {
        messaging.addListener({
          message: Constants.HAND_RAISE,
          callback: controller.handRaiseChanged,
        });
      }

      messaging.addListener({
        message: Constants.LOG_STUDENT_OUT,
        callback: controller.studentLogoutConfirmed,
      });

      messaging.subscribeToMessages({
        channel: `${room.getRoomName().toLowerCase()}-student`,
        user: user,
      });

      model.set('listening', true);
    }

    activity.refresh(controller.activityCheckDone);
    header.render();
  },

  activityChanged: function () {
    controller.closeModal('feedback-shim');
    controller.closeModal('results-shim');

    if (model.isNewActivity()) {
      model.reset();
      if (model.requiresRosterLogin()) {
        controller.redirectToStudentIdRoute();
        return;
      }
      activity.loadQuizAndResponses(controller.prepareQuizAndResponses);
    } else if (controller.isReady()) {
      controller.doNextRoute();
    }
  },

  closeModal: function (id) {
    let element = document.getElementById(id);

    if (element) {
      ReactDOM.unmountComponentAtNode(element);
      element.parentNode.removeChild(element);
      utils.unblurPage();
    }
  },

  redirectToStudentIdRoute: function () {
    const roomName = (
      room.getRoomName() ||
      user.getRoomName() ||
      ''
    ).toLowerCase();
    if (!roomName) {
      router.routeToStudentLogin();
      return;
    }

    // Ensure student logout notifies the backend with the room name before redirecting.
    room.setRoomName(roomName);
    user.logout(() => {
      window.location.href = `/login/student/#student-id/${roomName}`;
    });
  },

  handRaiseChanged: function (data) {
    try {
      data = JSON.parse(data);
    } catch (e) {
      console.warn(e);
    }

    if (data.state === 'enabled' || data.state === 'disabled') {
      user.setHandraiseStatus(data.state);
    }

    if (data.state === 'on' || data.state === 'off') {
      if (user.get('id') === data.id) {
        if (data.state === 'on') {
          user.setStudentHandraise(true);
        } else {
          user.setStudentHandraise(false);
        }
      }
    }

    header.render();
  },

  activityCheckDone: function () {
    if (!model.isActivityRunning()) {
      model.setReady();
      controller.doNextRoute();
    }
  },

  prepareQuizAndResponses: function () {
    const randomSeed = user.getRandomSeed();
    const extraRandom = activity.get('id');
    const shuffle = shuffler({
      seed: randomSeed,
      extraRandom: extraRandom,
    });

    if (model.hasRandomQuestions()) {
      model.shuffleQuestions(shuffle);
    } else {
      model.sortQuestions();
    }

    let questions = model.getQuestions();
    let responses = model.getResponsesObject();
    for (let i = 0; i < questions.length; i++) {
      let question = questions[i];
      let response = responses[question.question_id];
      model.setQuestionResponse(question, response);
      if (model.hasRandomAnswers()) {
        model.shuffleAnswers(question, shuffle);
      } else {
        model.sortAnswers(question);
      }
    }

    model.setReady();
    controller.doNextRoute();
  },

  doneResponding: function () {
    return (
      model.isStudentFinished() ||
      (model.allQuestionsAnswered() &&
        !(model.allowRepeatResponses() || model.isStudentNav()))
    );
  },

  shouldWaitForTeacherPaced: function () {
    if (
      activity.isSpaceRace() ||
      model.allowRepeatResponses() ||
      !activity.isTeacherPaced()
    ) {
      return false;
    }
    return model.answeredTeacherPaced();
  },

  shouldDoWait: function () {
    return (
      !model.isActivityRunning() ||
      controller.doneResponding() ||
      controller.shouldWaitForTeacherPaced() ||
      activity.isActivityPaused()
    );
  },

  shouldDoName: function () {
    return (
      !activity.isVote() &&
      activity.needsStudentName() &&
      !(model.hasRosterName() || model.haveStudentInputName())
    );
  },

  shouldDoTeam: function () {
    return (
      !controller.shouldDoName() &&
      activity.isSpaceRace() &&
      model.shouldPickTeam() &&
      !model.haveStudentTeam()
    );
  },

  shouldDoQuiz: function () {
    return (
      !controller.shouldDoWait() &&
      !controller.shouldDoName() &&
      !controller.shouldDoTeam()
    );
  },

  showFooter: function () {
    let footer = document.getElementById('footer');
    if (footer) {
      footer.style.visibility = 'visible';
    }
  },

  routeToWait: function () {
    router.routeToWait();
  },

  doNextRoute: function () {
    if (controller.shouldDoWait()) {
      controller.showFooter();
      router.routeToWait();
      return;
    }

    if (!activity.isVote()) {
      if (activity.needsStudentName()) {
        if (user.hasRosterName()) {
          if (!model.hasRosterName()) {
            model.setRosterName(user.getRosterName());
            activity.setStudentName(
              user.getRosterName(),
              controller.doNextRoute
            );
            return;
          }
        } else if (!model.haveStudentInputName()) {
          controller.showFooter();
          router.routeToName();
          return;
        }
      } else if (!model.haveStudentInputName()) {
        model.setRandomName();
      }
    }

    if (activity.isSpaceRace()) {
      if (model.shouldPickTeam() && !model.haveStudentTeam()) {
        controller.showFooter();
        router.routeToTeam();
        return;
      } else {
        if (!model.haveStudentTeam()) {
          model.getAutoAssignedTeam(controller.doNextRoute);
          return;
        }
      }
    }

    if (model.isStudentNav()) {
      model.initStudentNavIndex();
    }

    router.routeToQuiz();
  },

  confirmLogout: function () {
    popup.render({
      title: translate('Please Confirm'),
      message: translate('Are you sure you want to sign out?'),
      buttonText: translate('Yes'),
      cancelText: translate('No'),
      buttonClicked: function () {
        controller.studentLogoutConfirmed();
      },
    });
  },

  studentLogoutConfirmed: function (message) {
    if (message === 'room_cleared') {
      user.logout(router.routeToStudentLogin);
    } else {
      try {
        message = JSON.parse(message);
      } catch (e) {
        console.warn(e);
      }

      if (message.kick && message.kick == user.getUserUuid()) {
        user.logout(router.routeToStudentLogin);
      }
    }
  },
};

let controller = new RootStudentController();
module.exports = controller;
